import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {NavBar, Popup} from 'antd-mobile';
import {LeftOutline} from 'antd-mobile-icons';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import { useTranslation } from 'react-i18next';
import {closeNavigationModals as closeNavigationModalsUtil, forceCloseNavigationModal, emitCloseNavigationEvent} from '../../../utils/navigationUtils';
import {useDeliveryTaskOrderList, useRouteColorMap, useRetryBlockControl, preload} from '../store/deliveryStore';
import {useFailedUploadRetry} from '../delivering/hooks/useFailedUploadRetry';
import MapboxMapTemporary from './MapboxMapViewTemporary';
import EmptyState from 'components/EmptyState';
import OrderDetailTemporaryCard, { OrderDetailTemporaryCardRef } from './OrderDetailTemporaryCard';
import ClusterAddressListCard from './ClusterAddressListCard';
import AccessInfoDetailView from '../delivering/components/AccessInfoDetailView';
import AccessCodeFormView from '../delivering/components/AccessCodeFormView';
import PODAbnormalAlert from 'components/PODAbnormalAlert';
import { DeliveringPath, DeliveryDetailPath } from 'enums/DeliveryNavigatePathEnum';
import { setShouldCheckPendingCodes } from '../delivering/utils/accessCodeValidationState';
import {useLazyQuery} from '@apollo/client';
import {ANALYZE_ADDRESS_SIMPLE, AnalyzeAddressSimpleResponse} from 'graphql/queries/addressQueries';
import {useConfigEnable} from 'graphql/queries/commonConfigQueries';
import {GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE} from 'constants/grayConfigKeys';
import { MapBoxPageMarker } from 'constants/mapBoxTrackLog';

// 10种不同deliveryTaskId颜色
const MARKER_COLORS = [
    '#000000', '#668B8B', '#2F4F4F', '#483D8B', '#BEBEBE',
    '#548B54', '#8B864E', '#CD9B1D', '#A2B5CD', '#8B8B7A',
];


/**
 * ⚠️ TODO() 派送端 “地图模式” web版只维护到7.1.0，已迁移原生（这个页面不要再更新了）
 */


// 派送失败的颜色
const FAILED_DELIVERY_COLOR = '#EF4444';

// 默认颜色
const DEFAULT_MARKER_COLOR = '#f5f5f5';

const MapModeTemporaryPage: React.FC = () => {
    console.log('DeliveringTemporaryPage');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    // 获取当前司机派送的订单列表
    const { deliveryTaskOrderList, refreshDeliveryTaskOrderList } = useDeliveryTaskOrderList();
    const { routeColorMap, getOrAssignRouteColor } = useRouteColorMap();
    // 上传重试阻塞逻辑
    const { failedUploadCount, isRetrying, showRetryingHint, isRetryReady } = useFailedUploadRetry();
    const { setRetryBlocked, isRetryBlocked } = useRetryBlockControl();

    // Popup 控制状态
    const [popupVisible, setPopupVisible] = useState(false);
    const [selectedMarkerData, setSelectedMarkerData] = useState<any>(null);
    const [hasRestoredPopup, setHasRestoredPopup] = useState(false);
    // 视图模式：'addresses' 显示集群地址列表，'card' 显示订单详情卡片
    const [viewMode, setViewMode] = useState<'addresses' | 'card'>('card');
    // Popup 内容视图：'order' 显示订单详情，'accessCode' 显示门禁详情，'addForm' 显示新增表单，'updateForm' 显示更新表单
    const [popupContentView, setPopupContentView] = useState<'order' | 'accessCode' | 'addForm' | 'updateForm'>('order');
    // 选中的地址订单列表（从地址列表点击后进入）
    const [selectedAddressOrders, setSelectedAddressOrders] = useState<any[] | null>(null);
    // 门禁详情页需要的参数
    const [accessCodeViewParams, setAccessCodeViewParams] = useState<{
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    } | null>(null);
    // 表单页需要的参数
    const [formViewParams, setFormViewParams] = useState<{
        mode: 'add' | 'update';
        code?: any; // AccessCode
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    } | null>(null);
    // 刷新触发器，用于触发门禁详情页刷新
    const [accessCodeRefreshTrigger, setAccessCodeRefreshTrigger] = useState(0);
    // 地址分析相关状态
    const [analyzedAddressLine1, setAnalyzedAddressLine1] = useState<string | undefined>(undefined);
    const [analyzeAddress, { loading: analyzingAddress }] = useLazyQuery<AnalyzeAddressSimpleResponse>(ANALYZE_ADDRESS_SIMPLE);
    // 导航地址标准化功能灰度配置
    const { isEnabled: isNavigationAddressAnalyzeEnabled, loading: loadingGrayConfig } = useConfigEnable(GRAY_CONFIG_NAVIGATION_ADDRESS_ANALYZE);

    // 根据重试状态设置数据获取阻塞
    useEffect(() => {
        if (!isRetryReady) {
            return;
        }
        const shouldBlock = failedUploadCount > 0 || showRetryingHint;
        setRetryBlocked(shouldBlock);
    }, [failedUploadCount, isRetryReady, isRetrying, setRetryBlocked, showRetryingHint]);

    // 初始化时检查重试状态并刷新订单列表
    const hasInitialRefreshRun = useRef(false);
    useEffect(() => {
        if (!isRetryReady || hasInitialRefreshRun.current) {
            return;
        }
        hasInitialRefreshRun.current = true;
        // 只有在重试数据初始化完成后才执行校验逻辑
        preload();
        const timer = setTimeout(() => {
            if (failedUploadCount <= 0 && !showRetryingHint){
                refreshDeliveryTaskOrderList();
            }
        }, 100);

        // 清理定时器，防止内存泄漏
        return () => clearTimeout(timer);
    }, [failedUploadCount, isRetryReady, refreshDeliveryTaskOrderList, showRetryingHint]);

    // 保存选中的标记数据到 sessionStorage，以便返回时恢复
    const saveSelectedMarkerToStorage = useCallback((data: any) => {
        sessionStorage.setItem('selectedMarkerData', JSON.stringify(data));
    }, []);

    // 保存完整的弹窗状态（包括视图模式和选中的地址订单）
    const savePopupState = useCallback(() => {
        const popupState = {
            viewMode,
            selectedAddressOrders,
            selectedMarkerData
        };
        sessionStorage.setItem('mapModePopupState', JSON.stringify(popupState));
        console.log('💾 保存弹窗状态:', popupState);
    }, [viewMode, selectedAddressOrders, selectedMarkerData]);

    const getSelectedMarkerFromStorage = useCallback(() => {
        const saved = sessionStorage.getItem('selectedMarkerData');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.warn('Failed to parse saved marker data:', error);
            }
        }
        return null;
    }, []);

    const clearSelectedMarkerFromStorage = useCallback(() => {
        sessionStorage.removeItem('selectedMarkerData');
        sessionStorage.removeItem('selectAddressSession');
        sessionStorage.removeItem('originalClusterAddresses');
        sessionStorage.removeItem('orderDetailScrollTop');
        sessionStorage.removeItem('orderDetailCardScrollTop');
        sessionStorage.removeItem('batch_flow_completed');
    }, []);

    // 过滤出有效的订单（派送中或派送失败），并用 store 中的最新数据替换
    const filterValidClusterAddresses = (clusterAddresses: any[]): any[] => {
        return clusterAddresses
            .map((order: any) => {
                // 从 store 中找到最新的订单数据
                const currentOrder = deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut);
                return currentOrder || null; // 返回最新数据或 null
            })
            .filter((currentOrder: any) => {
                // 过滤出符合条件的订单
                if (currentOrder) {
                    const isDeliveryFailed = currentOrder.isAbnormal && !currentOrder.orderWithAddress?.order?.isReturnToWhsScanned && currentOrder.isDelivery && (!currentOrder.orderWithAddress?.order?.isDelivered || !currentOrder.orderWithAddress?.order?.isFinished);
                    const isDelivering = currentOrder.isPickedUp && !currentOrder.isAbnormal && currentOrder.isDelivery && (!currentOrder.orderWithAddress?.order?.isDelivered || !currentOrder.orderWithAddress?.order?.isFinished);
                    return isDeliveryFailed || isDelivering;
                }
                return false;
            });
    };

    // 处理配送地址数据
    const deliveryAddresses = useMemo(() => {
        // 首先进行与列表页面相同的初始过滤
        const deliveringData = deliveryTaskOrderList.filter(item => {
            return (item.isPickedUp || item.isAbnormal || item.isDelivery) && !item.isTransferOut;
        });

        // 然后进行与列表页面相同的二次过滤
        const filteredOrders = deliveringData.filter(order => {
            // 派送中：已取件且非异常且未派送成功
            const isDelivering = order.isPickedUp && !order.isAbnormal && order.isDelivery && (!order.orderWithAddress?.order?.isDelivered || !order.orderWithAddress?.order?.isFinished);
            // 派送失败：异常且未派送成功且未扫描退回仓库
            const isDeliveryFailed = order.isAbnormal && !order.orderWithAddress?.order?.isReturnToWhsScanned && order.isDelivery && (!order.orderWithAddress?.order?.isDelivered || !order.orderWithAddress?.order?.isFinished);

            return isDelivering || isDeliveryFailed;
        });

        // 使用全局 store 中的 routeColorMap，首次进入按 palette 分配，后续返回复用
        const addresses = filteredOrders
            .filter(order => order.orderWithAddress?.deliveryAddress)
            .map(order => {
                // 获取deliveryTaskId
                const deliveryTaskId = order.deliveryTaskId ? order.deliveryTaskId.toString() : 'default';

                // 为所有订单分配颜色
                let localMarkerColor = undefined;

                if (order.isAbnormal) {
                    // 派送失败的订单使用固定颜色
                    localMarkerColor = FAILED_DELIVERY_COLOR;
                } else {
                    // 仅读取 store 中已有颜色，避免在 render 中写 store；缺失时用默认色，由 useEffect 滞后写入
                    localMarkerColor = routeColorMap[deliveryTaskId] ?? DEFAULT_MARKER_COLOR;
                }

                // 使用与HereMapView相同的地址格式，包含完整地址信息
                const address = order.orderWithAddress?.deliveryAddress?.standardizedAddressString || [
                    order.orderWithAddress.deliveryAddress.addressLine1 || '',
                    order.orderWithAddress.deliveryAddress.addressLine2 || '',
                    order.orderWithAddress.deliveryAddress.city || '',
                    order.orderWithAddress.deliveryAddress.state || '',
                    order.orderWithAddress.deliveryAddress.zipcode || '',
                    'US'
                ].filter(Boolean).join(' ');

                return {
                    id: order.id.toString(),
                    address: address,
                    trackingNumber: order.trackingNumber,
                    deliveryTaskOrderId: order.id,
                    stopNumber: order.stopNumber,
                    subStopNumber: order.subStopNumber,
                    isAbnormal: order.isAbnormal, // 异常状态字段
                    localMarkerColor, // 新增颜色字段
                    latitude: order.orderWithAddress?.deliveryAddress?.latitude,
                    longitude: order.orderWithAddress?.deliveryAddress?.longitude,
                    standardizedAddressString: address,
                    deliverAt: order.orderWithAddress?.order?.deliverAt,
                    taskDate: order.taskDate,
                    fullName: order.orderWithAddress?.deliveryAddress?.fullName,
                    phoneNumber: order.orderWithAddress?.deliveryAddress?.phoneNumber,
                    routeName: order.routeName,
                    orderWithAddress: order.orderWithAddress,
                };
            })
            .sort((a, b) => {
                // 首先按照routeNumber排序（如果有的话）
                // 然后按照stopNumber排序，确保与列表中的顺序一致
                return a.stopNumber - b.stopNumber;
            });

        return addresses;
    }, [deliveryTaskOrderList, routeColorMap]);

    // 在 commit 阶段写入 routeColorMap，避免在 render 中调用 getOrAssignRouteColor 触发 setState
    useEffect(() => {
        const deliveringData = deliveryTaskOrderList.filter(item => {
            return (item.isPickedUp || item.isAbnormal || item.isDelivery) && !item.isTransferOut;
        });
        const filteredOrders = deliveringData.filter(order => {
            const isDelivering = order.isPickedUp && !order.isAbnormal && order.isDelivery && (!order.orderWithAddress?.order?.isDelivered || !order.orderWithAddress?.order?.isFinished);
            const isDeliveryFailed = order.isAbnormal && !order.orderWithAddress?.order?.isReturnToWhsScanned && order.isDelivery && (!order.orderWithAddress?.order?.isDelivered || !order.orderWithAddress?.order?.isFinished);
            return isDelivering || isDeliveryFailed;
        });
        filteredOrders.forEach(order => {
            if (order.isAbnormal) return;
            const deliveryTaskId = order.deliveryTaskId ? order.deliveryTaskId.toString() : 'default';
            getOrAssignRouteColor(deliveryTaskId, MARKER_COLORS);
        });
    }, [deliveryTaskOrderList, getOrAssignRouteColor]);

    // 地图引用，用于直接操作地图
    const mapRef = useRef<any>(null);
    const orderDetailCardRef = useRef<OrderDetailTemporaryCardRef>(null);

    // 保存地图状态到 sessionStorage
    const saveMapState = useCallback(() => {
        if (mapRef.current) {
            const state = {
                center: [mapRef.current.getCenter().lng, mapRef.current.getCenter().lat],
                zoom: mapRef.current.getZoom(),
                timestamp: Date.now()
            };
            sessionStorage.setItem('mapModePageState', JSON.stringify(state));
            console.log('Saved map state:', state);
        }
    }, []);

    // 获取保存的地图状态
    const getSavedMapState = () => {
        const savedState = sessionStorage.getItem('mapModePageState');

        // 有保存的状态则恢复（从其他页面返回）
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                console.log('📍 恢复保存的地图状态:', state);
                return state;
            } catch (error) {
                console.warn('Failed to parse saved map state:', error);
            }
        }
        
        // 没有保存的状态（首次进入或刷新页面）
        console.log('❌ 没有保存的状态，返回 null，地图将重置');
        return null;
    };

    // 处理标记点击事件 - 显示弹窗
    const handleMarkerClick = (markerData: any) => {
        // ✅ 清理可能残留的标记，防止白屏
        sessionStorage.removeItem('returningFromDetail');
        sessionStorage.removeItem('pickUpSuccess');
        sessionStorage.removeItem('mapModePopupState');

        // 当切换stop时，重置popup内容视图为订单详情页
        // 这样可以避免显示上一个stop的code详情页
        setPopupContentView('order');
        setAccessCodeViewParams(null);
        setFormViewParams(null);
        setSelectedAddressOrders(null);

        // 关闭长按导航弹窗
        handleCloseNavigationModals();

        if (markerData.isCluster) {
            const freshMarkerData = {
                ...markerData
            };

            // 🎯 检查是否所有订单都是相同的标准化地址
            const addresses = markerData.clusterAddresses || [];
            const uniqueAddresses = new Set(
                addresses.map((order: any) =>
                    order.orderWithAddress?.deliveryAddress?.standardizedAddressString ||
                    order.standardizedAddressString ||
                    order.address
                )
            );

            // 如果只有一个唯一地址，直接显示订单详情卡片
            if (uniqueAddresses.size === 1) {
                setSelectedMarkerData(freshMarkerData);
                setSelectedAddressOrders(addresses);
                setViewMode('card');
                setPopupVisible(true);
                saveSelectedMarkerToStorage(freshMarkerData);
            } else {
                setSelectedMarkerData(freshMarkerData);
                setViewMode('addresses');
                setPopupVisible(true);
                saveSelectedMarkerToStorage(freshMarkerData);
            }
        } else {
            const singleMarkerData = {
                ...markerData,
                isCluster: false
            };
            setSelectedMarkerData(singleMarkerData);
            setViewMode('card');
            setPopupVisible(true);
            saveSelectedMarkerToStorage(singleMarkerData);
        }
        // 新增：强制滚动到顶部
        setTimeout(() => {
            // 方法1：查找 Popup 内的滚动容器
            const popupBody = document.querySelector('.adm-popup-body');
            if (popupBody) {
                popupBody.scrollTop = 0;
            }

            // 方法2：查找内部的滚动容器
            const scrollContainer = document.querySelector('.max-h-\\[70vh\\]');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
            }

            // 方法3：直接查找所有可滚动元素
            const scrollableElements = document.querySelectorAll('*');
            scrollableElements.forEach(element => {
                if (element.scrollHeight > element.clientHeight) {
                    element.scrollTop = 0;
                }
            });
        }, 0);
    };

    // 处理地址列表中的地址点击 - 进入该地址的订单详情
    const handleAddressClick = useCallback((address: string, orders: any[]) => {
        setSelectedAddressOrders(orders);
        setViewMode('card'); // 切换到订单详情卡片模式
    }, []);

    // 处理从订单详情返回到地址列表
    const handleBackToAddressList = useCallback(() => {
        setSelectedAddressOrders(null);
        setViewMode('addresses');
    }, []);

    // 处理地图点击事件 - 关闭弹窗和长按导航弹窗
    const handleMapClick = () => {
        handleClosePopup();
    };

    // 统一关闭导航弹窗逻辑（iOS 18 增强修复）
    const handleCloseNavigationModals = useCallback(() => {
        // 方式1：通过全局事件重置所有卡片组件的状态（解决集群模式问题）
        emitCloseNavigationEvent();
        // 方式2：单个订单模式：通过 ref 关闭
        orderDetailCardRef.current?.closeLongPressModal();
        // 方式3：集群模式：直接调用工具函数关闭所有导航弹窗（确保集群模式下也能关闭，iOS 18 增强）
        closeNavigationModalsUtil();

        // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
        requestAnimationFrame(() => {
            forceCloseNavigationModal(
                t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                t('pages.delivery.orderDetail.mapCancel') || undefined
            );
        });
    }, [t]);

    // 统一关闭弹窗逻辑（包含 iOS 18 增强修复）
    const handleClosePopup = useCallback(() => {
        // 精确关闭导航弹窗（同步执行，确保导航弹窗状态被正确清除）
        handleCloseNavigationModals();

        // 如果是“跳转流程导致的关闭”，需要保留 marker 数据，方便返回时自动恢复弹窗
        const preserveSelectedMarkerForReturn = sessionStorage.getItem('mapMode_preserve_selected_marker') === 'true';
        if (preserveSelectedMarkerForReturn) {
            sessionStorage.removeItem('mapMode_preserve_selected_marker');
        }

        // 先关闭弹窗，让关闭动画开始
        setPopupVisible(false);

        // 延迟重置其他状态，等待弹窗关闭动画完成（Ant Design Mobile 的 Popup 关闭动画大约 300ms）
        setTimeout(() => {
            setSelectedMarkerData(null);
            setSelectedAddressOrders(null); // ✅ 清理选中的地址订单，防止白屏
            setViewMode('card');
            setPopupContentView('order'); // Reset to order view on close
            setAccessCodeViewParams(null); // Clear access code params
            setFormViewParams(null); // Clear form params
            if (!preserveSelectedMarkerForReturn) {
                clearSelectedMarkerFromStorage();
                sessionStorage.removeItem('originalClusterAddresses');
            }
            setHasRestoredPopup(false);
        }, 350);
    }, [handleCloseNavigationModals, clearSelectedMarkerFromStorage]);

    // 按钮点击前的回调 - 保存地图状态
    const handleBeforeNavigate = useCallback(() => {
        saveMapState();
        savePopupState(); // 保存弹窗状态
    }, [saveMapState, savePopupState]);

    // 处理返回导航逻辑
    const handleBackNavigation = useCallback(() => {
        // 返回前保存地图状态（中心点、zoom 等）
        saveMapState();

        // 🎯 第一层：如果当前在订单详情卡片模式，且是从地址列表进入的（有 selectedAddressOrders）
        // 则回退到地址列表，而不是关闭弹窗
        if (viewMode === 'card' && selectedAddressOrders && selectedMarkerData?.isCluster) {
            // 检查是否所有订单都是相同地址（直接跳过了地址列表）
            const addresses = selectedMarkerData.clusterAddresses || [];
            const uniqueAddresses = new Set(
                addresses.map((order: any) =>
                    order.orderWithAddress?.deliveryAddress?.standardizedAddressString ||
                    order.standardizedAddressString ||
                    order.address
                )
            );

            // 如果只有一个唯一地址，说明直接跳过了地址列表，应该直接关闭弹窗
            if (uniqueAddresses.size === 1) {
                handleClosePopup();
                return;
            }

            // 有多个不同地址，返回地址列表
            handleBackToAddressList();
            return;
        }

        // 🎯 第二层：如果当前在地址列表模式，关闭弹窗（留在地图页面）
        if (viewMode === 'addresses' && selectedMarkerData?.isCluster) {
            handleClosePopup();
            return;
        }

        // 🎯 第三层：如果弹窗是打开的（无论是单地址还是其他情况），先关闭弹窗
        if (popupVisible) {
            handleClosePopup();
            return;
        }

        // 🎯 第四层：弹窗已关闭，返回派送列表
        // 检查是否从DriverViolationReviewPage返回
        const fromDriverViolationReview = location.state?.from === 'DriverViolationReviewPage';

        if (fromDriverViolationReview) {
            // 从DriverViolationReviewPage返回，直接跳转到delivering页面
            navigate("/delivery/delivering/temporary");
        } else {
            navigate("/delivery/delivering/temporary");
        }
    }, [viewMode, selectedAddressOrders, selectedMarkerData, popupVisible, handleBackToAddressList, handleClosePopup, navigate, location.state, saveMapState]);

    // 处理导航到门禁详情（地图模式）
    const handleNavigateToAccessCodeDetail = useCallback((params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => {
        console.log('切换到门禁详情视图', params);
        setAccessCodeViewParams(params);
        setPopupContentView('accessCode');
    }, []);

    // 处理导航到新增表单（地图模式）
    const handleNavigateToAddForm = useCallback((params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => {
        console.log('切换到新增表单视图', params);
        setFormViewParams({
            mode: 'add',
            ...params
        });
        setPopupContentView('addForm');
    }, []);

    // 处理导航到更新表单（地图模式）
    const handleNavigateToUpdateForm = useCallback((params: {
        code: any; // AccessCode
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => {
        console.log('切换到更新表单视图', params);
        setFormViewParams({
            mode: 'update',
            ...params
        });
        setPopupContentView('updateForm');
    }, []);

    // 当selectedMarkerData变化时，调用地址分析API
    useEffect(() => {
        // 重置地址分析结果
        setAnalyzedAddressLine1(undefined);

        if (!selectedMarkerData) {
            return;
        }

        // 获取地址信息（优先从集群的第一个地址获取，如果不是集群则从单个标记获取）
        let deliveryAddress: any = null;

        if (selectedMarkerData.isCluster && selectedMarkerData.clusterAddresses && selectedMarkerData.clusterAddresses.length > 0) {
            // 从deliveryTaskOrderList中查找第一个订单的完整信息
            const firstOrderId = selectedMarkerData.clusterAddresses[0].deliveryTaskOrderId;
            const firstOrder = deliveryTaskOrderList.find(item => item.id === firstOrderId);
            deliveryAddress = firstOrder?.orderWithAddress?.deliveryAddress;
        } else if (selectedMarkerData.deliveryTaskOrderId) {
            // 单个标记的情况
            const order = deliveryTaskOrderList.find(item => item.id === selectedMarkerData.deliveryTaskOrderId);
            deliveryAddress = order?.orderWithAddress?.deliveryAddress;
        }

        if (!deliveryAddress?.addressLine1 || !deliveryAddress?.city || !deliveryAddress?.state || !deliveryAddress?.zipcode) {
            return;
        }

        // 检查灰度配置，如果未启用则不调用地址分析API
        if (!isNavigationAddressAnalyzeEnabled) {
            return;
        }

        // 调用地址分析API
        analyzeAddress({
            variables: {
                streetAddress: deliveryAddress.addressLine1,
                unit: deliveryAddress.addressLine2 || null,
                city: deliveryAddress.city,
                state: deliveryAddress.state,
                zipCode: deliveryAddress.zipcode
            }
        }).then(response => {
            if (response.data?.analyzeAddressSimple?.entityComponents) {
                const components = response.data.analyzeAddressSimple.entityComponents;
                // 组合标准化解析后的字段：line1 + city + state + zipcode，state 和 zipcode 之间用空格，其他用逗号分隔
                if (components?.addressLine1) {
                    const stateZip = [components.stateAbbreviation, components.zipcode].filter(Boolean).join(' ');
                    const parts = [components.addressLine1, components.cityName, stateZip].filter(Boolean);
                    setAnalyzedAddressLine1(parts.join(', '));
                }
            }
        }).catch(error => {
            console.error('Failed to analyze address:', error);
            // 分析失败不影响主流程，保持静默
        });
    }, [selectedMarkerData, deliveryTaskOrderList, analyzeAddress, isNavigationAddressAnalyzeEnabled]);

    // 只在从详情页返回时恢复弹窗状态
    useEffect(() => {
        const isReturningFromDetail = sessionStorage.getItem('returningFromDetail') === 'true';
        const isPickUpSuccess = sessionStorage.getItem('pickUpSuccess') === 'true';
        if (!isReturningFromDetail || hasRestoredPopup) {
            return;
        }

        const savedMarkerData = getSelectedMarkerFromStorage();

        // 🎯 如果没有保存的 marker 数据，直接返回
        if (!savedMarkerData) {
            console.log('⚠️ 没有保存的 marker 数据，跳过恢复');
            setHasRestoredPopup(true);
            return;
        }

        if (isReturningFromDetail) {
            if (isPickUpSuccess) {
                // ✅ 派送成功的情况
                if (savedMarkerData.isCluster) {
                    // 🎯 直接过滤 savedMarkerData.clusterAddresses，找出有效订单（派送中或派送失败）
                    const ordersAtSameLocation = (savedMarkerData.clusterAddresses || [])
                        .map((originalOrder: any) => {
                            // 从 store 中找到最新的订单数据
                            const currentOrder = deliveryTaskOrderList.find(item => item.id === originalOrder.deliveryTaskOrderId && !item.isTransferOut);
                            if (!currentOrder) return null;

                            // 🎯 合并原始订单的字段（保留 standardizedAddressString 等字段）
                            return {
                                ...originalOrder, // 保留原始订单的所有字段
                                ...currentOrder,  // 用最新状态覆盖
                                // 确保关键字段不被覆盖
                                standardizedAddressString: originalOrder.standardizedAddressString ||
                                    currentOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString,
                                orderWithAddress: currentOrder.orderWithAddress
                            };
                        })
                        .filter((order: any) => {
                            if (!order) return false;
                            // 派送失败
                            const isDeliveryFailed = order.isAbnormal && !order.orderWithAddress?.order?.isReturnToWhsScanned && order.isDelivery && (!order.orderWithAddress?.order?.isDelivered || !order.orderWithAddress?.order?.isFinished);
                            // 派送中
                            const isDelivering = order.isPickedUp && !order.isAbnormal && order.isDelivery && (!order.orderWithAddress?.order?.isDelivered || !order.orderWithAddress?.order?.isFinished);
                            return isDeliveryFailed || isDelivering;
                        });

                    if (ordersAtSameLocation.length > 0) {
                        // 🎯 检查是否是从地址列表点击进来的（有 selectedAddressOrders）
                        const savedPopupStateStr = sessionStorage.getItem('mapModePopupState');
                        let wasFromAddressList = false;
                        let originalSelectedAddress = '';

                        if (savedPopupStateStr) {
                            try {
                                const savedPopupState = JSON.parse(savedPopupStateStr);
                                wasFromAddressList = !!savedPopupState.selectedAddressOrders;
                                if (wasFromAddressList && savedPopupState.selectedAddressOrders.length > 0) {
                                    // 获取原始选中的地址
                                    originalSelectedAddress = savedPopupState.selectedAddressOrders[0].orderWithAddress?.deliveryAddress?.standardizedAddressString ||
                                        savedPopupState.selectedAddressOrders[0].standardizedAddressString ||
                                        savedPopupState.selectedAddressOrders[0].address;
                                }
                            } catch (error) {
                                console.error('解析 savedPopupState 失败:', error);
                            }
                        }

                        const freshMarkerData = {
                            isCluster: true,
                            clusterAddresses: ordersAtSameLocation,
                            longitude: savedMarkerData.longitude,
                            latitude: savedMarkerData.latitude
                        };
                        setSelectedMarkerData(freshMarkerData);

                        if (wasFromAddressList && originalSelectedAddress) {
                            // 🎯 从地址列表点击进来的：检查当前地址是否还有剩余订单
                            const remainingOrdersAtCurrentAddress = ordersAtSameLocation.filter((order: any) => {
                                const orderAddress = order.orderWithAddress?.deliveryAddress?.standardizedAddressString ||
                                    order.standardizedAddressString ||
                                    order.address;
                                return orderAddress === originalSelectedAddress;
                            });

                            console.log(`📍 当前地址 "${originalSelectedAddress}" 剩余订单数:`, remainingOrdersAtCurrentAddress.length);

                            if (remainingOrdersAtCurrentAddress.length > 0) {
                                // 当前地址还有剩余订单，回到 card 视图显示剩余订单
                                console.log(`✅ 当前地址还有 ${remainingOrdersAtCurrentAddress.length} 单，回到 card 视图`);
                                setSelectedAddressOrders(remainingOrdersAtCurrentAddress);
                                setViewMode('card');
                            } else {
                                // 当前地址没有剩余订单，检查是否还有其他地址
                                const uniqueAddresses = new Set(
                                    ordersAtSameLocation.map((order: any) =>
                                        order.orderWithAddress?.deliveryAddress?.standardizedAddressString ||
                                        order.standardizedAddressString ||
                                        order.address
                                    )
                                );

                                if (uniqueAddresses.size > 1) {
                                    // 还有其他地址，回到地址列表
                                    console.log(`✅ 当前地址已完成，还有 ${uniqueAddresses.size} 个其他地址，回到地址列表`);
                                    setSelectedAddressOrders(null);
                                    setViewMode('addresses');
                                } else if (uniqueAddresses.size === 1) {
                                    // 只剩一个地址，直接显示该地址的订单
                                    console.log(`✅ 只剩一个地址，直接显示订单详情`);
                                    setSelectedAddressOrders(ordersAtSameLocation);
                                    setViewMode('card');
                                }
                            }
                        } else {
                            // 🎯 直接点击聚合 marker 进来的：检查是否所有订单都是相同地址
                            const uniqueAddresses = new Set(
                                ordersAtSameLocation.map((order: any) =>
                                    order.orderWithAddress?.deliveryAddress?.standardizedAddressString ||
                                    order.standardizedAddressString ||
                                    order.address
                                )
                            );

                            if (uniqueAddresses.size === 1) {
                                // 所有订单都是相同地址，直接显示订单详情
                                console.log(`✅ 所有订单都是相同地址，直接显示订单详情`);
                                setSelectedAddressOrders(ordersAtSameLocation);
                                setViewMode('card');
                            } else {
                                // 有多个不同地址，回到地址列表
                                console.log(`✅ 有 ${uniqueAddresses.size} 个不同地址，回到地址列表`);
                                setSelectedAddressOrders(null);
                                setViewMode('addresses');
                            }
                        }

                        setPopupVisible(true);
                        setHasRestoredPopup(true);
                    } else {
                        // 没有订单了，关闭弹窗
                        console.log('✅ 该经纬度没有剩余订单，关闭弹窗');
                        clearSelectedMarkerFromStorage();
                        setPopupVisible(false);
                        setHasRestoredPopup(true);
                    }
                } else {
                    // 🎯 单地址marker：直接关闭弹窗
                    console.log('✅ 单地址marker派送成功，关闭弹窗');
                    clearSelectedMarkerFromStorage();
                    setPopupVisible(false);
                    setHasRestoredPopup(true);
                }
            } else {
                // ❌ 中途返回：恢复到跳转之前的状态
                console.log('❌ 中途返回，恢复到跳转之前的状态');
                const savedPopupStateStr = sessionStorage.getItem('mapModePopupState');

                if (savedPopupStateStr) {
                    try {
                        const savedPopupState = JSON.parse(savedPopupStateStr);

                        // 恢复选中的地址订单（如果有）
                        if (savedPopupState.selectedAddressOrders) {
                            console.log('💾 恢复 selectedAddressOrders，数量:', savedPopupState.selectedAddressOrders.length);
                            const validAddressOrders = savedPopupState.selectedAddressOrders
                                .map((order: any) => deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut) || order)
                                .filter((order: any) => order);

                            if (validAddressOrders.length > 0) {
                                console.log('💾 有效 selectedAddressOrders 数量:', validAddressOrders.length);
                                setSelectedAddressOrders(validAddressOrders);

                                // 🎯 如果只有 1 单，应该返回地址列表（因为是从地址列表点击进来的）
                                // 所以需要恢复地址列表视图，而不是 card 视图
                                if (validAddressOrders.length === 1) {
                                    console.log('💾 只有 1 单，恢复到地址列表');
                                    // 需要恢复完整的 marker 数据（包括所有地址）
                                    if (savedMarkerData.isCluster && savedMarkerData.clusterAddresses) {
                                        const validClusterAddresses = savedMarkerData.clusterAddresses
                                            .map((order: any) => deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut) || order)
                                            .filter((order: any) => order);

                                        if (validClusterAddresses.length > 0) {
                                            setSelectedMarkerData({
                                                ...savedMarkerData,
                                                clusterAddresses: validClusterAddresses
                                            });
                                        }
                                    }
                                    // 返回地址列表视图
                                    setViewMode('addresses');
                                } else {
                                    console.log('💾 有多单，恢复到 card 视图');
                                    // 🎯 多单：更新 clusterAddresses
                                    if (savedMarkerData.isCluster && savedMarkerData.clusterAddresses) {
                                        const validClusterAddresses = savedMarkerData.clusterAddresses
                                            .map((order: any) => deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut) || order)
                                            .filter((order: any) => order);

                                        if (validClusterAddresses.length > 0) {
                                            setSelectedMarkerData({
                                                ...savedMarkerData,
                                                clusterAddresses: validClusterAddresses
                                            });
                                        }
                                    }
                                    setViewMode(savedPopupState.viewMode || 'card');
                                }
                            }
                        } else {
                            console.log('💾 没有 selectedAddressOrders');
                            // 🎯 没有 selectedAddressOrders，说明是直接点击聚合 marker（所有订单相同地址）
                            if (savedMarkerData.isCluster && savedMarkerData.clusterAddresses) {
                                const validClusterAddresses = savedMarkerData.clusterAddresses
                                    .map((order: any) => deliveryTaskOrderList.find(item => item.id === order.deliveryTaskOrderId && !item.isTransferOut) || order)
                                    .filter((order: any) => order);

                                if (validClusterAddresses.length > 0) {
                                    setSelectedMarkerData({
                                        ...savedMarkerData,
                                        clusterAddresses: validClusterAddresses
                                    });
                                }
                            } else {
                                // 单订单模式：直接设置 savedMarkerData
                                console.log('💾 单订单模式，设置 savedMarkerData');
                                setSelectedMarkerData(savedMarkerData);
                            }
                            setViewMode(savedPopupState.viewMode || 'card');
                        }

                        setPopupVisible(true);
                        setHasRestoredPopup(true);
                    } catch (error) {
                        console.error('恢复状态失败:', error);
                    }
                }
            }
        }

        // 延迟清理标记
        setTimeout(() => {
            sessionStorage.removeItem('returningFromDetail');
            sessionStorage.removeItem('pickUpSuccess');
            sessionStorage.removeItem('mapModePopupState');
        }, 600);
    }, [deliveryTaskOrderList, getSelectedMarkerFromStorage, clearSelectedMarkerFromStorage, hasRestoredPopup]);

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* Navbar - 固定在顶部 */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBackNavigation}>
                    {t('pages.useMenu.mapMode')}
                </NavBar>
            </div>

            {/* POD异常提示 - 固定高度容器 */}
            <div className="flex-none">
                <PODAbnormalAlert navigateState={{ from: 'MapModePage' }} />
            </div>

            {/* 地图容器 - 占据剩余空间 */}
            <div className="flex-1 relative">
                {deliveryAddresses.length > 0 ? (
                    <MapboxMapTemporary
                        deliveryAddresses={deliveryAddresses}
                        onMarkerClick={handleMarkerClick}
                        onMapClick={handleMapClick}
                        mapRef={mapRef}
                        getSavedMapState={getSavedMapState}
                        pageMarker={MapBoxPageMarker.DELIVERY_MAP_MODE}
                    />
                ) : (
                    <EmptyState />
                )}
            </div>

            {/* Popup 弹窗 */}
            <Popup
                visible={popupVisible}
                position="bottom"
                mask={false}
                bodyStyle={{
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    height: viewMode === 'addresses' ? 'auto' : '80vh',
                    maxHeight: '80vh'
                }}
                onClose={handleClosePopup}
            >
                {selectedMarkerData && (
                    <div style={{ height: '100%' }}>
                        {popupContentView === 'accessCode' && accessCodeViewParams ? (
                            <div className="flex flex-col h-full">
                                <div className="flex-none flex items-center px-4 py-3 border-b border-gray-200 bg-white">
                                    <button
                                        onClick={() => {
                                            setPopupContentView('order');
                                            setAccessCodeViewParams(null);
                                        }}
                                        className="flex items-center gap-1 text-base text-gray-900 active:opacity-70"
                                    >
                                        <LeftOutline fontSize={20} />
                                    </button>
                                    <div className="flex-1 text-center text-base text-gray-900">
                                        {t('pages.delivery.orderDetail.referenceAccessInfo')}
                                    </div>
                                    <div className="w-8"></div>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <AccessInfoDetailView
                                        orderId={accessCodeViewParams.orderId}
                                        addressId={accessCodeViewParams.addressId}
                                        trackingNumber={accessCodeViewParams.trackingNumber}
                                        address={accessCodeViewParams.address}
                                        addressLat={accessCodeViewParams.addressLat}
                                        addressLon={accessCodeViewParams.addressLon}
                                        refreshTrigger={accessCodeRefreshTrigger}
                                        onBack={() => {
                                            // 设置标记，返回orderdetail页时检查待验证code
                                            setShouldCheckPendingCodes(true);
                                            setPopupContentView('order');
                                            setAccessCodeViewParams(null);
                                        }}
                                        onNavigateToAdd={(params) => {
                                            handleNavigateToAddForm({
                                                orderId: params.orderId,
                                                addressId: params.addressId,
                                                trackingNumber: params.trackingNumber,
                                                address: params.address,
                                                addressLat: params.addressLat,
                                                addressLon: params.addressLon
                                            });
                                        }}
                                        onNavigateToUpdate={(params) => {
                                            handleNavigateToUpdateForm({
                                                code: params.code,
                                                orderId: params.orderId,
                                                addressId: params.addressId,
                                                trackingNumber: params.trackingNumber,
                                                address: params.address,
                                                addressLat: params.addressLat,
                                                addressLon: params.addressLon
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (popupContentView === 'addForm' || popupContentView === 'updateForm') && formViewParams ? (
                            <div className="flex flex-col h-full">
                                <div className="flex-none flex items-center px-4 py-3 border-b border-gray-200 bg-white">
                                    <button
                                        onClick={() => {
                                            // 如果是从详情页进入的，返回到详情页；否则返回到订单页
                                            if (accessCodeViewParams) {
                                                setPopupContentView('accessCode');
                                            } else {
                                                setPopupContentView('order');
                                            }
                                            setFormViewParams(null);
                                        }}
                                        className="flex items-center gap-1 text-base text-gray-900 active:opacity-70"
                                    >
                                        <LeftOutline fontSize={20} />
                                    </button>
                                    <div className="flex-1 text-center text-base font-semibold">
                                        {popupContentView === 'addForm'
                                            ? t('pages.delivery.orderDetail.addAccessCode')
                                            : t('pages.delivery.orderDetail.updateAccessCodeTitle')
                                        }
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    <AccessCodeFormView
                                        mode={formViewParams.mode}
                                        code={formViewParams.code}
                                        addressId={formViewParams.addressId}
                                        trackingNumber={formViewParams.trackingNumber}
                                        address={formViewParams.address}
                                        addressLat={formViewParams.addressLat}
                                        addressLon={formViewParams.addressLon}
                                        orderId={formViewParams.orderId}
                                        onBack={() => {
                                            // 如果是从详情页进入的，返回到详情页；否则返回到订单页
                                            if (accessCodeViewParams) {
                                                setPopupContentView('accessCode');
                                            } else {
                                                setPopupContentView('order');
                                            }
                                            setFormViewParams(null);
                                        }}
                                        onSuccess={() => {
                                            // 判断返回目标：
                                            // 1. 如果 accessCodeViewParams 存在，说明是从 codeList 进入的，返回 codeList
                                            // 2. 如果 accessCodeViewParams 不存在，说明是从卡片进入的（第一次新增），返回卡片
                                            if (accessCodeViewParams) {
                                                // 从 codeList 进入的场景：返回 codeList
                                                // 触发详情页刷新
                                                setAccessCodeRefreshTrigger(prev => prev + 1);
                                                // 设置标记，返回到详情页时检查待验证code
                                                setShouldCheckPendingCodes(true);
                                                // 返回到详情页（codeList）
                                                setPopupContentView('accessCode');
                                            } else {
                                                // 从卡片进入的场景（第一次新增）：返回卡片
                                                // 不设置标记，因为用户刚新增，可能还没有尝试，不需要立即弹框收集反馈
                                                // 返回到订单卡片
                                                setPopupContentView('order');
                                            }
                                            setFormViewParams(null);
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* 🎯 地址列表视图 - 显示集群中的所有地址 */}
                                {viewMode === 'addresses' && selectedMarkerData?.isCluster && (
                                    <ClusterAddressListCard
                                        clusterAddresses={selectedMarkerData.clusterAddresses}
                                        onAddressClick={handleAddressClick}
                                    />
                                )}

                                {/* 订单详情卡片视图 */}
                                {viewMode === 'card' && (
                                    <div className="flex flex-col h-full">
                                        <div className="flex-1 overflow-y-auto">
                                            {(() => {
                                                console.log('🎯 渲染 card 视图');
                                                console.log('📍 selectedAddressOrders:', selectedAddressOrders);
                                                console.log('📍 selectedMarkerData:', selectedMarkerData);

                                                // 过滤有效订单的工具函数，并用 store 中的最新数据替换
                                                const getValidOrders = (orders: any[]) => {
                                                    return orders
                                                        .map(order => {
                                                            // 从 store 中找到最新的订单数据
                                                            const latestOrder = deliveryTaskOrderList.find(
                                                                item => item.id === order.deliveryTaskOrderId
                                                            );
                                                            return latestOrder || order; // 如果找到最新数据就用最新的，否则用原数据
                                                        })
                                                        .filter(order => order); // 过滤掉不存在的订单
                                                };

                                                // 🎯 如果是从地址列表点击进来的，显示该地址的订单
                                                if (selectedAddressOrders) {
                                                    const validOrders = getValidOrders(selectedAddressOrders);

                                                    // 🎯 如果只有 1 单，使用单订单模式（不传 clusterInfo）
                                                    if (validOrders.length === 1) {
                                                        console.log('📍 只有 1 单，使用单订单模式, deliveryTaskOrderId:', validOrders[0].id);
                                                        return (
                                                            <div className="h-full overflow-y-auto">
                                                                <div className="h-full">
                                                                    <OrderDetailTemporaryCard
                                                                        deliveryTaskOrderId={validOrders[0].id}
                                                                        trackingNumber={validOrders[0].trackingNumber || ''}
                                                                        onClose={handleBackToAddressList}
                                                                        onBeforeNavigate={handleBeforeNavigate}
                                                                        fromMapMode={true}
                                                                        onNavigateToAccessCodeDetail={handleNavigateToAccessCodeDetail}
                                                                        onNavigateToAddForm={handleNavigateToAddForm}
                                                                        popupContentView={popupContentView}
                                                                        analyzedAddressLine1={analyzedAddressLine1}
                                                                        isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // 🎯 多单情况：使用集群模式
                                                    console.log('📍 有 ' + validOrders.length + ' 单，使用集群模式, deliveryTaskOrderId:', validOrders[0].id);
                                                    return (
                                                        <div className="h-full overflow-y-auto">
                                                            <div className="h-full">
                                                                <OrderDetailTemporaryCard
                                                                    deliveryTaskOrderId={validOrders[0].id}
                                                                    trackingNumber={validOrders[0].trackingNumber || ''}
                                                                    onClose={handleBackToAddressList}
                                                                    onBeforeNavigate={handleBeforeNavigate}
                                                                    fromMapMode={true}
                                                                    onNavigateToAccessCodeDetail={handleNavigateToAccessCodeDetail}
                                                                    onNavigateToAddForm={handleNavigateToAddForm}
                                                                    clusterInfo={{
                                                                        isCluster: true,
                                                                        clusterCount: validOrders.length,
                                                                        clusterAddresses: validOrders
                                                                    }}
                                                                    popupContentView={popupContentView}
                                                                    analyzedAddressLine1={analyzedAddressLine1}
                                                                    isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // 处理单个订单场景
                                                return (
                                                    <OrderDetailTemporaryCard
                                                        ref={orderDetailCardRef}
                                                        deliveryTaskOrderId={selectedMarkerData.deliveryTaskOrderId}
                                                        trackingNumber={selectedMarkerData.trackingNumber || ''}
                                                        onClose={handleClosePopup}
                                                        onBeforeNavigate={handleBeforeNavigate}
                                                        fromMapMode={true}
                                                        onNavigateToAccessCodeDetail={handleNavigateToAccessCodeDetail}
                                                        onNavigateToAddForm={handleNavigateToAddForm}
                                                        popupContentView={popupContentView}
                                                        analyzedAddressLine1={analyzedAddressLine1}
                                                        isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                                    />
                                                );

                                            })()}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

            </Popup>
        </div>
    );
};

export default MapModeTemporaryPage; 